import React, { useEffect, useState, useCallback, useMemo } from "react";
import ListMenu from "./ListMenu/ListMenu";
import "./List.css";
import useSize from "../../../../Utils/useSize/useSize";
import Backend from "../../../../Backend/Backend";
import toast from "react-hot-toast";
import Filter from "./Filter/Filter";
import Loading from "../../../../Utils/Loading/Loading";
import NotFound from "../../../../Utils/NotFound/NotFound";
import ListCard from "./ListCard";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const fmt = require("indian-number-format");
const backend = await new Backend().init();

export default function UserList({ master_id, userType }) {
  const windowSize = useSize();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userLimit, setUserLimit] = useState(0);
  const [depositLimit, setDepositLimit] = useState(0);
  const [depositLimitTotal, setDepositLimitTotal] = useState(0);

  // 🧩 Derived variables memoized
  const base = useMemo(
    () => ({
      server: localStorage.getItem("server"),
      id: localStorage.getItem("id"),
      username: localStorage.getItem("username"),
      token: localStorage.getItem("token"),
    }),
    []
  );

  // ⚡ Optimized user loader
  const loadUser = useCallback(async () => {
    setLoading(true);

    const data =
      userType === 5
        ? { ...base, broker_id: localStorage.getItem("id") }
        : userType === 3
        ? { ...base, master_id: localStorage.getItem("id") }
        : userType === 2
        ? master_id
          ? { ...base, master_id }
          : { ...base, super_id: localStorage.getItem("id") }
        : master_id
        ? { ...base, master_id }
        : { ...base, admin_id: localStorage.getItem("id") };

    try {
      const res = await backend.load_user(data);
      if (res.error === "False") {
        const userData = res.user || [];
        setUsers(userData);
        setFilteredUsers(userData);
        setUserLimit(res.user_limit || 0);
        setDepositLimit(res.deposit_limit || 0);
        setDepositLimitTotal(res.deposit_limit_total || 0);
      } else if (res.logout === 1) {
        window.LogOut?.();
      } else {
        toast.error(res.message || "Failed to load users");
      }
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Server error. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [base, master_id, userType]);

  // 🔁 Load users initially
  useEffect(() => {
    loadUser();
    window.loadUser = loadUser;
  }, [loadUser]);

  // 🔍 Optimized search handler (debounced logic)
  const search = useCallback(
    (value) => {
      if (!value || value.trim().length < 2) {
        setFilteredUsers(users);
        return;
      }

      const v = value.toLowerCase();
      const filtered = users.filter((u) =>
        u.username?.toLowerCase().includes(v)
      );
      setFilteredUsers(filtered);
    },
    [users]
  );

  // 📥 Lazy-load + optimized PDF generation
  const downloadUserPDF = useCallback(async () => {
    if (!filteredUsers.length)
      return toast.error("No data available to export");

    try {
      // ✅ Correct dynamic import for pdfmake
      const pdfMake = (await import("pdfmake/build/pdfmake")).default;
      const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;

      // ✅ Set virtual fonts
      pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs;

      const body = [
        [
          userType === 1 ? { text: "Super", style: "tableHeader" } : null,
          userType === 1 || userType === 2
            ? { text: "Master", style: "tableHeader" }
            : null,
          { text: "Username", style: "tableHeader" },
          { text: "Booked", style: "tableHeader" },
          { text: "NetBooked", style: "tableHeader" },
          { text: "Credit", style: "tableHeader" },
          { text: "Current", style: "tableHeader" },
          { text: "Trade", style: "tableHeader" },
          { text: "Close Only", style: "tableHeader" },
          { text: "Status", style: "tableHeader" },
          { text: "IP Address", style: "tableHeader" },
          { text: "Created Date", style: "tableHeader" },
          { text: "Last Login", style: "tableHeader" },
        ].filter(Boolean),
        ...filteredUsers.map((i) =>
          [
            userType === 1 ? { text: i.super_username } : null,
            userType === 1 || userType === 2
              ? { text: i.master_username }
              : null,
            { text: i.username },
            {
              text: fmt.format(i.profit_loss),
              color: i.profit_loss > 0 ? "#399918" : "#FF0000",
            },
            {
              text: fmt.format(i.net_profit_loss),
              color: i.net_profit_loss > 0 ? "#399918" : "#FF0000",
            },
            { text: fmt.format(i.deposit_total) },
            { text: fmt.format(i.deposit_current) },
            { text: i.trade === 1 ? "Active" : "Deactive" },
            { text: i.only_sq === 1 ? "Active" : "Deactive" },
            { text: i.status === 1 ? "Active" : "Deactive" },
            { text: i.ip || "-" },
            {
              text: i.date_created
                ? moment(i.date_created).format("DD-MM-YYYY HH:mm:ss")
                : "-",
            },
            {
              text: i.last_login_date
                ? moment(i.last_login_date).format("DD-MM-YYYY HH:mm:ss")
                : "-",
            },
          ].filter(Boolean)
        ),
      ];

      // ✅ Create and trigger download
      pdfMake
        .createPdf({
          pageSize: "A3",
          pageOrientation: "landscape",
          pageMargins: [40, 60, 40, 60],
          content: [
            {
              text: `${window.APP_NAME} USER LIST`,
              style: "header",
              alignment: "center",
            },
            {
              text: `Generated on: ${moment().format("DD-MMM-YYYY HH:mm")}`,
              style: "subHeader",
            },
            {
              table: {
                headerRows: 1,
                widths: Array(body[0].length).fill("auto"),
                body,
              },
              layout: "lightHorizontalLines",
            },
          ],
          styles: {
            header: { fontSize: 18, bold: true },
            subHeader: {
              fontSize: 10,
              color: "#64748B",
              margin: [0, 0, 0, 10],
            },
            tableHeader: {
              bold: true,
              fontSize: 11,
              color: "#FFFFFF",
              fillColor: "#1E293B",
              alignment: "center",
            },
          },
          footer: (current, total) => ({
            text: `Page ${current} of ${total}`,
            alignment: "center",
            fontSize: 9,
          }),
        })
        .download(`${moment().format("DD-MMM")}-User-List.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF. Check console for details.");
    }
  }, [filteredUsers, userType]);

  // 📊 Optimized Excel generation
  const downloadExcel = useCallback(() => {
    if (!filteredUsers.length) return toast.error("No data available");

    const data = filteredUsers.map((i) => ({
      ...(userType === 1 && { Super: i.super_username }),
      ...((userType === 1 || userType === 2) && { Master: i.master_username }),
      Username: i.username,
      Booked: i.profit_loss,
      NetBooked: i.net_profit_loss,
      CreditTotal: i.deposit_total,
      Current: i.deposit_current,
      Trade: i.trade === 1 ? "Active" : "Deactive",
      CloseOnly: i.only_sq === 1 ? "Active" : "Deactive",
      Status: i.status === 1 ? "Active" : "Deactive",
      IP: i.ip,
      Created: moment(i.date_created).format("DD-MM-YYYY HH:mm:ss"),
      LastLogin: moment(i.last_login_date).format("DD-MM-YYYY HH:mm:ss"),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      `User-${moment().format("DD-MMM-YYYY")}.xlsx`
    );
  }, [filteredUsers, userType]);

  // 🖥️ UI Rendering
  return (
    <div style={{ height: windowSize[1] - 160, width: windowSize[0] }}>
      <Filter
        downloadUserPDF={downloadUserPDF}
        downloadExcel={downloadExcel}
        master_id={master_id}
        records={filteredUsers.length}
        user_limit={userLimit}
        deposit_limit={depositLimit}
        deposit_limit_total={depositLimitTotal}
        search={search}
      />
      <ListMenu userType={userType} />

      <div
        style={{
          width: windowSize[0],
          height: windowSize[1] - 220,
          overflowY: "auto",
        }}
      >
        {loading ? (
          <div className="loadingContainer">
            <Loading height={windowSize[1] - 220} />
          </div>
        ) : filteredUsers.length ? (
          filteredUsers.map((item, index) => (
            <ListCard
              userType={userType}
              key={item.id || index}
              item={item}
              index={index}
              reload={loadUser}
              filteredUsers={filteredUsers}
            />
          ))
        ) : (
          <NotFound
            height={windowSize[1] - 220}
            title="Nothing here"
            subtext="No User Found. Use Filter. "
          />
        )}
      </div>
    </div>
  );
}
